<template>
  <ModuleWrapper
    :moduleId="moduleId"
    :image="imageSrcPath('assets/branding/logo.svg')"
    title="Header Settings"
    class="tw-relative"
    @initializeData="initializeData"
  >
    <template #main>
      <div class="tw-mb-4 tw-block tw-text-xs">
        Keep any content in image centered and leave at least 20% margin on all sides.
      </div>
      <div>
        <StudioSelect
          id="background-type"
          label="Background Type"
          :options="[
            { value: 'Solid Color', label: 'Solid Color' },
            { value: 'Image', label: 'Image' },
            { value: 'Gradient', label: 'Gradient' }
          ]"
          size="small"
          orientation="horizontal"
          class="tw-mb-4"
          :value="selectedBackgroundType"
          @change="onSelectBackgroundType"
        />
        <ImageUploadButton
          v-if="selectedBackgroundType === 'Image'"
          label="Min of 1024x768, Max of 2400x1800"
          :image="splashHeroBackground"
          :acceptedTypes="['.png', '.jpg', '.jpeg', '.ico', '.webp']"
          :s3BucketPath="s3BucketPath"
          :imageValidation="backgroundImageValidationDesktop"
          :security="filestackSecurity"
          required
          class="tw-mb-4"
          @changeImageUrl="onImageUrlChange"
        />
        <ConditionalColorPicker
          v-else-if="selectedBackgroundType === 'Solid Color'"
          id="background-color-picker"
          label="Background Color"
          type="color"
          class="tw-mb-4"
          :initialColor="splashSolidColor.style"
          @colorChange="onColorChange"
        />
        <StudioColorPickerInput
          v-else-if="selectedBackgroundType === 'Gradient'"
          id="background-gradient-picker"
          label="Background Gradient"
          type="gradient"
          class="tw-mb-4"
          :initialGradient="splashGradient"
          @gradientChange="onGradientChange"
        />
      </div>
      <div>
        <FontModule :moduleId="moduleId" />
      </div>

      <div>
        <StudioToggleWithLabel
          id="show-title-toggle"
          label="Show Title"
          class="tw-mb-4"
          :checked="is_title_visible"
          @update:checked="toggleTitleVisible"
        />
      </div>
      <div>
        <StudioToggleWithLabel
          id="show-date-toggle"
          label="Show Date"
          class="tw-mb-4"
          :checked="is_date_visible"
          @update:checked="toggleDateVisible"
        />
      </div>
      <div>
        <StudioToggleWithLabel
          id="show-logo-toggle"
          label="Show Logo"
          class="tw-mb-4"
          :checked="is_logo_visible"
          @update:checked="toggleLogoVisible"
        />
      </div>

      <StudioToggleWithLabel
        v-if="selectedBackgroundType === 'Image'"
        id="hide-gradient-toggle"
        label="Background Overlay"
        class="tw-mb-4"
        :checked="is_overlay_visible"
        @update:checked="toggleBackgroundOverlay"
      />
    </template>
  </ModuleWrapper>
</template>

<script>
import logger from '@goldcast/logger';
import { mapGetters, mapMutations, mapState } from 'vuex';

import ModuleWrapper from './ModuleWrapper.vue';
import { gradient } from '../data/defaultValues';
import FontModule from './FontModule/CustomFont.vue';
import ConditionalColorPicker from '../components/ConditionalColorPicker.vue';
import ImageUploadButton from '../components/ImageUploadButton.vue';

import { getFilestackSecurity } from '@/components/core/Filestack/filestackSecurity';
import { GET_RESOURCES_PATH, BACKGROUND_IMG_VALIDATION_DESKTOP, BACKGROUND_IMG_VALIDATION_MOBILE } from '@/constants';
import StudioColorPickerInput from '@/studioComponents/StudioColorPickerInput.vue';
import StudioSelect from '@/studioComponents/StudioSelect.vue';
import StudioToggleWithLabel from '@/studioComponents/StudioToggleWithLabel.vue';
import { destructureRgbColor } from '@/utils/color.ts';
import {
  destructureGradientColor,
  isColorString,
  isUrlString,
  isGradientString,
  destructureUrl
} from '@/utils/utils.js';

const cloneDeep = require('lodash.clonedeep');

const color = {
  red: 255,
  green: 255,
  blue: 255,
  alpha: 0.2,
  style: 'rgba(255, 255, 255, 0.2)'
};

export default {
  name: 'SplashV2HeroBackground',
  components: {
    ModuleWrapper,
    ConditionalColorPicker,
    StudioColorPickerInput,
    StudioSelect,
    StudioToggleWithLabel,
    ImageUploadButton,
    FontModule
  },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectedBackgroundType: 'Solid Color',
      s3BucketPath: `${GET_RESOURCES_PATH(this.$route.params.eventId)}`,
      splashSolidColor: color,
      splashGradient: cloneDeep(gradient),
      splashHeroBackground: '',
      is_title_visible: true,
      is_date_visible: true,
      is_logo_visible: false,
      titleColor: color,
      backgroundImageValidationDesktop: BACKGROUND_IMG_VALIDATION_DESKTOP,
      backgroundImageValidationMobile: BACKGROUND_IMG_VALIDATION_MOBILE,
      is_overlay_visible: false,
      lastOverlayState: false,
      filestackSecurity: null
    };
  },
  async mounted() {
    this.initializeData();
    if (this.$store.getters['org/isSignedFilestackUploadsEnabled']) {
      try {
        const { security } = await getFilestackSecurity({
          intent: 'event_asset',
          assetKind: 'image',
          event: this.$route.params.eventId
        });
        this.filestackSecurity = security;
      } catch (e) {
        logger.warn('Failed to fetch Filestack security, upload will fail', e);
      }
    }
  },
  computed: {
    ...mapGetters('branding', {
      getInitialModuleData: 'getInitialModuleData'
    }),
    ...mapState('branding', ['isCurrentModuleInValid', 'changesMadeInThemeObj', 'currentView']),

    isImageBackgroundSelected() {
      return this.selectedBackgroundType === 'Image';
    },

    isFormValid() {
      if (this.isImageBackgroundSelected) {
        return this.splashHeroBackground;
      }
      return true;
    }
  },

  watch: {
    isFormValid(newInputs) {
      this.setCurrentModuleInvalid(!newInputs);
    },
    selectedBackgroundType: {
      immediate: true,
      handler(newType) {
        this.updateHeroBackgrounds(newType);
      }
    }
  },

  methods: {
    ...mapMutations('branding', {
      setChangesMadeInThemeObj: 'setChangesMadeInThemeObj',
      setChangesMadeInEvent: 'setChangesMadeInEvent',
      setCurrentModuleInvalid: 'setCurrentModuleInvalid'
    }),
    initializeData() {
      const moduleKeys = [
        'splash_page_hero',
        'event_background',
        'is_title_visible',
        'is_date_visible',
        'is_logo_visible',
        'is_overlay_visible'
      ];
      const {
        splash_page_hero: splashPageHero,
        event_background: eventBackground,
        is_title_visible: is_title_visible,
        is_date_visible: is_date_visible,
        is_logo_visible: is_logo_visible,
        is_overlay_visible: is_overlay_visible
      } = this.getInitialModuleData(moduleKeys);

      const isSplashGradient = isGradientString(splashPageHero);
      const isSplashSolidColor = isColorString(splashPageHero);

      if (isSplashGradient) {
        this.splashGradient = destructureGradientColor(splashPageHero);
        this.selectedBackgroundType = 'Gradient';
        this.splashSolidColor = color;
        this.is_overlay_visible = false;
      } else if (isSplashSolidColor) {
        this.splashSolidColor = destructureRgbColor(splashPageHero);
        this.selectedBackgroundType = 'Solid Color';
        this.is_overlay_visible = false;
        this.splashGradient = cloneDeep(gradient);
      } else {
        this.selectedBackgroundType = 'Image';
        this.splashSolidColor = color;
        this.splashGradient = cloneDeep(gradient);
        this.is_overlay_visible = is_overlay_visible ?? false;
        this.splashHeroBackground = !this.splashHeroBackground
          ? destructureUrl(eventBackground)
          : destructureUrl(splashPageHero);
      }

      this.is_title_visible = is_title_visible || false;
      this.is_date_visible = is_date_visible || false;
      this.is_logo_visible = is_logo_visible ?? false;
    },

    updateHeroBackgrounds(selectedType) {
      const moduleKeys = ['splash_page_hero', 'event_background'];
      const { splash_page_hero, event_background } = this.getInitialModuleData(moduleKeys);

      if (selectedType === 'Image') {
        this.splashHeroBackground = isUrlString(splash_page_hero)
          ? destructureUrl(splash_page_hero)
          : destructureUrl(event_background);
      } else {
        this.splashHeroBackground = '';
      }
    },

    onSelectBackgroundType(e) {
      const moduleKeys = ['event_background', 'mobile_event_background', 'is_overlay_visible'];
      const { event_background } = this.getInitialModuleData(moduleKeys);
      const selectedType = e.target.value;

      if (this.selectedBackgroundType === 'Image') {
        // Store the current overlay state before switching away
        this.lastOverlayState = this.is_overlay_visible;
      }

      if (selectedType === 'Gradient') {
        this.setChangesMadeInThemeObj({
          splash_page_hero: this.splashGradient.style,
          mobile_splash_page_hero: this.splashGradient.style,
          is_overlay_visible: false
        });
      } else if (selectedType === 'Solid Color') {
        this.setChangesMadeInThemeObj({
          splash_page_hero: this.splashSolidColor.style,
          mobile_splash_page_hero: this.splashSolidColor.style,
          is_overlay_visible: false
        });
      } else {
        this.splashHeroBackground ||= destructureUrl(event_background);
        this.setChangesMadeInThemeObj({
          splash_page_hero: `Url('${this.splashHeroBackground}')`,
          mobile_splash_page_hero: `Url('${this.splashHeroBackground}')`,
          is_overlay_visible: this.lastOverlayState ?? false
        });
      }

      this.selectedBackgroundType = selectedType;
    },

    onColorChange(color) {
      this.splashSolidColor = color;

      this.setChangesMadeInThemeObj({
        splash_page_hero: color.style,
        mobile_splash_page_hero: color.style
      });
    },

    onGradientChange(gradient) {
      this.splashGradient = gradient;
      this.setChangesMadeInThemeObj({
        splash_page_hero: gradient.style,
        mobile_splash_page_hero: gradient.style
      });
    },

    onImageUrlChange(url) {
      this.splashHeroBackground = url;
      this.setChangesMadeInThemeObj({
        splash_page_hero: `Url('${this.splashHeroBackground}')`,
        mobile_splash_page_hero: `Url('${this.splashHeroBackground}')`
      });
    },

    toggleTitleVisible() {
      this.is_title_visible = !this.is_title_visible;
      this.setChangesMadeInEvent({
        is_title_visible: this.is_title_visible
      });
    },

    toggleDateVisible() {
      this.is_date_visible = !this.is_date_visible;
      this.setChangesMadeInEvent({
        is_date_visible: this.is_date_visible
      });
    },

    toggleLogoVisible() {
      this.is_logo_visible = !this.is_logo_visible;
      this.setChangesMadeInEvent({
        is_logo_visible: this.is_logo_visible
      });
    },

    toggleBackgroundOverlay() {
      if (this.selectedBackgroundType === 'Image') {
        this.is_overlay_visible = !this.is_overlay_visible;
        this.setChangesMadeInThemeObj({
          is_overlay_visible: this.is_overlay_visible
        });
      }
    }
  }
};
</script>
